import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box, Card, Grid, Typography, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, InputAdornment, TableSortLabel
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LabelIcon from "@material-ui/icons/Label";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, height: "100%", overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    transition: "transform 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: { padding: "20px 20px 16px", height: "100%" },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 36, fontWeight: 800, lineHeight: 1 },
  metricSub: { fontSize: 12, marginTop: 6, opacity: 0.85 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, marginTop: 24 },
  chartCard: { borderRadius: 14, backgroundColor: "#fff", padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  tableCard: { borderRadius: 14, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableHead: { backgroundColor: theme.palette.primary.main },
  tableHeadCell: { color: "#fff !important", fontWeight: "700 !important", fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.5px" },
  tableRow: {
    "&:hover": { backgroundColor: "#f5f3ff" },
    "&:nth-of-type(even)": { backgroundColor: "#fafafa" }
  },
  tagDot: { width: 12, height: 12, borderRadius: "50%", display: "inline-block", flexShrink: 0 },
}));

const PERIOD_OPTIONS = [
  { label: "Todos os tempos", value: "all" },
  { label: "Últimos 7 dias", value: 7 },
  { label: "Últimos 30 dias", value: 30 },
  { label: "Personalizado", value: "custom" },
];

const fmt = (d) => format(new Date(d), "yyyy-MM-dd");
const fmtDisplay = (d) => format(new Date(d + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR });

const PIE_COLORS = ["#9f7aea", "#ed8936", "#38a169", "#4299e1", "#e53e3e", "#d69e2e", "#38b2ac", "#667eea", "#fc8181", "#f6ad55"];

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <Box style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
        <Box style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: p.payload?.color || "#9f7aea" }} />
        <Typography style={{ fontSize: 13, fontWeight: 700, color: "#2d3748" }}>{p.name}</Typography>
      </Box>
      <Typography style={{ fontSize: 18, fontWeight: 800, color: p.payload?.color || "#9f7aea" }}>{p.value} tickets</Typography>
      <Typography style={{ fontSize: 12, color: "#a0aec0" }}>{Math.round(p.payload?.percent * 100)}% do total</Typography>
    </Box>
  );
};

export default function RelatorioTags() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState("all");
  const [customStart, setCustomStart] = useState(fmt(subDays(new Date(), 30)));
  const [customEnd, setCustomEnd] = useState(fmt(new Date()));
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("tickets");
  const [sortDir, setSortDir] = useState("desc");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { kanban: 0 };
      if (period !== "all" && period !== "custom") {
        params.dateStart = fmt(subDays(new Date(), period));
        params.dateEnd = fmt(new Date());
      } else if (period === "custom") {
        params.dateStart = customStart;
        params.dateEnd = customEnd;
      }
      const { data } = await api.get("/tags", { params });
      setTags(Array.isArray(data) ? data : data?.tags || []);
    } catch {
      setTags([]);
    }
    setLoading(false);
  }, [period, customStart, customEnd]);

  useEffect(() => { if (period !== "custom") loadData(); }, [period]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const filtered = tags
    .filter(t => !search || (t.name || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let va = a[sortBy] ?? 0, vb = b[sortBy] ?? 0;
      if (typeof va === "string") { va = va.toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const totalTags = tags.length;
  const totalTickets = tags.reduce((s, t) => s + (t.tickets || 0), 0);
  const topTag = [...tags].sort((a, b) => (b.tickets || 0) - (a.tickets || 0))[0];

  const pieData = [...tags]
    .sort((a, b) => (b.tickets || 0) - (a.tickets || 0))
    .slice(0, 10)
    .map((t, i) => ({
      name: t.name,
      value: t.tickets || 0,
      color: t.color || PIE_COLORS[i % PIE_COLORS.length],
      percent: totalTickets > 0 ? (t.tickets || 0) / totalTickets : 0
    }))
    .filter(t => t.value > 0);

  const barData = [...tags]
    .sort((a, b) => (b.tickets || 0) - (a.tickets || 0))
    .slice(0, 8)
    .map((t, i) => ({
      name: (t.name || "").length > 14 ? (t.name || "").slice(0, 12) + "…" : t.name,
      value: t.tickets || 0,
      color: t.color || PIE_COLORS[i % PIE_COLORS.length]
    }));

  const getPeriodLabel = () => {
    if (period === "all") return "Todos os tempos";
    if (period === "custom") return `${fmtDisplay(customStart)} a ${fmtDisplay(customEnd)}`;
    return PERIOD_OPTIONS.find(o => o.value === period)?.label || "";
  };

  const handleExport = () => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const rows = filtered.map((t, i) =>
      `<tr><td>${i + 1}</td><td><span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${t.color || "#9f7aea"};margin-right:6px;vertical-align:middle"></span>${t.name}</td><td>${t.tickets || 0}</td></tr>`
    ).join("");
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/><title>Relatório de Etiquetas</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;background:#f5f5f5}
h1{font-size:26px;font-weight:800;margin-bottom:4px}p{color:#718096;margin-bottom:24px}
table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07)}
th{background:#9f7aea;color:#fff;padding:12px 16px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
td{padding:12px 16px;border-bottom:1px solid #f0f0f0;font-size:14px}tr:last-child td{border:none}
.footer{margin-top:32px;color:#a0aec0;font-size:12px}</style></head>
<body><h1>🏷️ Relatório de Etiquetas</h1><p>Período: ${getPeriodLabel()} · Exportado em ${now}</p>
<table><thead><tr><th>#</th><th>Etiqueta</th><th>Tickets</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">Total: ${totalTags} etiquetas · ${totalTickets} tickets</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio-tags-${fmt(new Date())}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Relatório de Etiquetas</Typography>
          <Typography className={classes.subtitle}>Distribuição de tickets por etiqueta de atendimento</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExport}
            disabled={loading || !tags.length}
            style={{ backgroundColor: "#9f7aea", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterCard}>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 150px" }}>
          <InputLabel>Período</InputLabel>
          <Select value={period} onChange={e => setPeriod(e.target.value)} label="Período">
            {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        {period === "custom" && (
          <>
            <TextField label="De" type="date" size="small" variant="outlined"
              value={customStart} onChange={e => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ width: 160 }} />
            <TextField label="Até" type="date" size="small" variant="outlined"
              value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ width: 160 }} />
            <Button variant="contained" size="small" onClick={loadData}
              style={{ backgroundColor: "#9f7aea", color: "#fff", textTransform: "none", borderRadius: 8 }}>
              Aplicar
            </Button>
          </>
        )}
        <TextField placeholder="Buscar etiqueta..." size="small" variant="outlined"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#a0aec0" }} /></InputAdornment> }}
          style={{ flex: "1 1 150px" }} />
        {loading && <CircularProgress size={20} />}
        {!loading && tags.length > 0 && (
          <Chip label={`${totalTags} etiquetas · ${getPeriodLabel()}`} size="small"
            style={{ backgroundColor: "#faf5ff", color: "#6b46c1", fontWeight: 600 }} />
        )}
      </Box>

      <Typography className={classes.sectionLabel}>Visão Geral</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#faf5ff,#e9d8fd)", borderTop: "3px solid #9f7aea" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#6b46c1" }}>Total de Etiquetas</Typography>
                <LabelIcon style={{ color: "#9f7aea", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#6b46c1" }}>{loading ? "—" : totalTags}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#6b46c1" }}>Etiquetas de atendimento cadastradas</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#fff3e5,#fbd38d)", borderTop: "3px solid #ed8936" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#c05621" }}>Total de Tickets</Typography>
                <LabelIcon style={{ color: "#ed8936", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#c05621" }}>{loading ? "—" : totalTickets}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#c05621" }}>Com pelo menos uma etiqueta</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#f0fff4,#c6f6d5)", borderTop: "3px solid #38a169" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#276749" }}>Etiqueta Mais Usada</Typography>
                <LabelIcon style={{ color: "#38a169", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 2 }}>
                {topTag?.color && <Box style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: topTag.color }} />}
                <Typography className={classes.metricValue} style={{ color: "#276749", fontSize: 20 }}>
                  {loading || !topTag ? "—" : topTag.name}
                </Typography>
              </Box>
              <Typography className={classes.metricSub} style={{ color: "#276749" }}>
                {topTag ? `${topTag.tickets || 0} tickets` : ""}
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Distribuição Visual</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Distribuição por Etiqueta (Top 10)</Typography>
            {loading || pieData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados de tickets por etiqueta</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={95}
                    dataKey="value" paddingAngle={2}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <ReTooltip content={<CustomPieTooltip />} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }}
                    formatter={(val) => <span style={{ color: "#4a5568" }}>{val}</span>} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Top 8 Etiquetas por Volume de Tickets</Typography>
            {loading || barData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={260}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <ReTooltip formatter={(val) => [`${val} tickets`, "Quantidade"]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={16}>
                    {barData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Todas as Etiquetas</Typography>
      <Card elevation={0} className={classes.tableCard}>
        <TableContainer>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell} style={{ width: 50 }}>#</TableCell>
                <TableCell className={classes.tableHeadCell}>
                  <TableSortLabel active={sortBy === "name"} direction={sortBy === "name" ? sortDir : "asc"}
                    onClick={() => handleSort("name")} style={{ color: "#fff" }}>
                    Etiqueta
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "tickets"} direction={sortBy === "tickets" ? sortDir : "desc"}
                    onClick={() => handleSort("tickets")} style={{ color: "#fff" }}>
                    Tickets
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Participação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ padding: 40 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ padding: 40, color: "#a0aec0" }}>
                    {search ? "Nenhuma etiqueta encontrada para a busca" : "Nenhuma etiqueta cadastrada"}
                  </TableCell>
                </TableRow>
              ) : filtered.map((tag, i) => {
                const participation = totalTickets > 0 ? Math.round(((tag.tickets || 0) / totalTickets) * 100) : 0;
                return (
                  <TableRow key={tag.id} className={classes.tableRow}>
                    <TableCell style={{ color: "#a0aec0", fontSize: 12 }}>{i + 1}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                        <Box className={classes.tagDot} style={{ backgroundColor: tag.color || "#9f7aea" }} />
                        <Typography style={{ fontWeight: 600, fontSize: 14 }}>{tag.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={tag.tickets || 0} size="small"
                        style={{ backgroundColor: (tag.color || "#9f7aea") + "22", color: tag.color || "#9f7aea", fontWeight: 700, minWidth: 44 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Box>
                        <Typography style={{ fontSize: 13, fontWeight: 600 }}>{participation}%</Typography>
                        <Box style={{ height: 4, backgroundColor: "#e2e8f0", borderRadius: 2, marginTop: 3, width: "100%" }}>
                          <Box style={{ height: "100%", width: `${participation}%`, backgroundColor: tag.color || "#9f7aea", borderRadius: 2 }} />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
